import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { DiagFormName, SubmitDiagnosticsService } from './submit-diagnostics.service';

/**
 * appSubmitDiag — attaches lightweight, capture-phase listeners to a <form> host
 * to record how the form is filled (typed / pasted / autofilled) and how the
 * submit button is clicked. Metadata only — never reads field values.
 *
 * Usage: <form [formGroup]="form" appSubmitDiag="jsa" ...>
 * The value is the API path segment / backend `form` token (jsa | jsapaper | ...).
 *
 * One delegated listener per event kind on the form covers every field inside it,
 * so this adds negligible overhead regardless of field count.
 */
@Directive({
  selector: '[appSubmitDiag]',
  standalone: true,
})
export class SubmitDiagDirective implements OnInit, OnDestroy {
  @Input('appSubmitDiag') form!: DiagFormName;

  private onInput = (e: Event): void => {
    this.diag.recordInput(this.form, (e as InputEvent).inputType ?? null);
  };

  private onClick = (e: Event): void => {
    const target = e.target as HTMLElement | null;
    const btn = target?.closest?.(
      'button[type="submit"], button:not([type]), input[type="submit"]',
    ) as HTMLButtonElement | HTMLInputElement | null;
    if (!btn) return;
    this.diag.recordSubmitClick(this.form, e.isTrusted, (btn as any).disabled === true);
  };

  constructor(
    private host: ElementRef<HTMLElement>,
    private diag: SubmitDiagnosticsService,
  ) {}

  ngOnInit(): void {
    const el = this.host.nativeElement;
    // Capture phase so we still observe clicks on buttons that are disabled
    // (disabled controls don't emit bubbling click events, but a capture-phase
    // listener on the ancestor form still sees the pointer/click target).
    el.addEventListener('input', this.onInput, true);
    el.addEventListener('click', this.onClick, true);
  }

  ngOnDestroy(): void {
    const el = this.host.nativeElement;
    el.removeEventListener('input', this.onInput, true);
    el.removeEventListener('click', this.onClick, true);
  }
}
